<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { Check, Copy, LoaderCircle, PenLine, Send, Sparkles, WandSparkles } from "@lucide/vue";
import { api } from "@/lib/api";

const router = useRouter();
const title = ref("海边的最后一班车");
const idea = ref("一首关于夏夜海边告别的华语流行歌，克制、不煽情，副歌容易记住");
const lyrics = ref("");
const styleTags = ref("");
const loading = ref(false);
const naming = ref(false);
const copied = ref(false);
const error = ref("");

async function generateTitle() {
  naming.value = true;
  error.value = "";
  try {
    const result = await api.songTitle({
      prompt: `${idea.value || "请根据现有歌词提炼作品主题"}${styleTags.value ? `。${styleTags.value}` : ""}`,
      lyrics: lyrics.value || undefined,
    });
    title.value = result.title;
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "AI 取名失败";
  } finally {
    naming.value = false;
  }
}

async function generate(mode: "write_full_song" | "edit") {
  loading.value = true;
  error.value = "";
  try {
    const result = await api.lyrics({
      prompt: mode === "edit" ? `${idea.value}。请优化押韵和副歌记忆点。` : idea.value,
      title: title.value,
      mode,
      lyrics: mode === "edit" ? lyrics.value : undefined,
    });
    title.value = result.songTitle;
    lyrics.value = result.lyrics;
    styleTags.value = result.styleTags;
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "歌词生成失败";
  } finally {
    loading.value = false;
  }
}

async function copyLyrics() {
  await navigator.clipboard.writeText(lyrics.value);
  copied.value = true;
  setTimeout(() => (copied.value = false), 1600);
}

function sendToCreate() {
  localStorage.setItem(
    "wavecraft-lyrics-draft",
    JSON.stringify({ title: title.value, prompt: styleTags.value || idea.value, lyrics: lyrics.value }),
  );
  router.push("/create");
}
</script>

<template>
  <div class="lyrics-workspace">
    <section class="panel lyrics-brief">
      <div class="section-heading border-bottom">
        <div><p class="eyebrow">WRITE ROOM</p><h3>告诉 AI 你想写什么</h3></div>
        <span class="outline-icon"><PenLine :size="18" /></span>
      </div>
      <div class="form-section">
        <div class="field title-generator-field compact-title-generator">
          <div class="title-field-heading">
            <label for="lyrics-title">歌名（可选）</label>
            <button class="title-magic-button" type="button" :disabled="naming || (!idea && !lyrics)" @click="generateTitle">
              <LoaderCircle v-if="naming" class="spin" :size="15" /><WandSparkles v-else :size="15" />
              {{ naming ? "正在取名…" : "AI 生成歌名" }}
            </button>
          </div>
          <div class="title-input-shell" :class="{ loading: naming }">
            <input id="lyrics-title" v-model="title" maxlength="80" placeholder="也可以交给 AI" />
            <span class="title-spark" aria-hidden="true"><Sparkles :size="17" /></span>
          </div>
        </div>
        <label class="field"><span>主题与写作方向</span><textarea v-model="idea" rows="6" maxlength="2000" /></label>
        <div class="prompt-examples">
          <span>灵感模板</span>
          <button @click="idea = '写给二十岁自己的信，华语民谣，真诚、不说教'">写给自己</button>
          <button @click="idea = '适合毕业季合唱，告别但充满希望，流行摇滚'">毕业季</button>
          <button @click="idea = '新中式意象，月光、长街与故人，含蓄的爱情'">新中式</button>
        </div>
      </div>
      <button class="button button-dark button-wide" :disabled="loading || !idea" @click="generate('write_full_song')">
        <LoaderCircle v-if="loading" class="spin" :size="18" /><Sparkles v-else :size="18" />
        {{ loading ? "正在组织段落与押韵…" : "生成完整歌词" }}
      </button>
      <p v-if="error" class="inline-error">{{ error }}</p>
    </section>

    <section class="panel lyrics-editor">
      <div class="section-heading border-bottom">
        <div><p class="eyebrow">STRUCTURED LYRICS</p><h3>{{ title || "未命名歌词" }}</h3></div>
        <div class="editor-actions">
          <button class="icon-text-button" :disabled="!lyrics" @click="copyLyrics"><Check v-if="copied" :size="15" /><Copy v-else :size="15" />{{ copied ? "已复制" : "复制" }}</button>
          <button class="icon-text-button" :disabled="!lyrics || loading" @click="generate('edit')"><Sparkles :size="15" />AI 润色</button>
        </div>
      </div>
      <div v-if="styleTags" class="tag-strip"><span v-for="tag in styleTags.split(',')" :key="tag">{{ tag.trim() }}</span></div>
      <textarea v-model="lyrics" class="lyrics-paper" maxlength="3500" placeholder="在这里开始你的故事……&#10;&#10;用一个自然空行进入下一段……&#10;&#10;写下最想让人记住的一句……" />
      <div class="editor-footer">
        <span>{{ lyrics.length }}/3500 字符</span>
        <button class="button button-lime button-sm" :disabled="!lyrics" @click="sendToCreate"><Send :size="15" />带去生成音乐</button>
      </div>
    </section>
  </div>
</template>
